/**
 * Org chooser presentational pieces — header/footer chrome, the org row
 * (numbered, monogram avatar, role badge, hover affordance), and the
 * skeleton/error states. Split from chooser.tsx to keep files and
 * functions small; no data fetching happens here.
 */
import { useNavigate } from '@tanstack/react-router'
import { formatDistanceToNowStrict } from 'date-fns'
import { ArrowRight, Plus, RefreshCw } from 'lucide-react'
import { useAuth } from '@/app/auth-context'
import type { Organization, OrgRole } from '@/domain'
import { Badge, Button, Skeleton } from '@/components/ui'
import type { BadgeTone } from '@/components/ui'

const ROLE_TONE: Record<OrgRole, BadgeTone> = {
  admin: 'brand',
  audit: 'info',
  member: 'neutral',
}

/* ───────────────────────── header / footer ───────────────────────── */

export function ChooserHeader({ email, count }: { email?: string; count?: number }) {
  return (
    <header>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
        <img src="/assets/logos/logo_mark.png" alt="" width={22} height={22} aria-hidden="true" />
        <span className="kicker">IntegraTrace · Organizations</span>
      </div>
      <h1
        style={{
          margin: 'var(--s-3) 0 var(--s-1)',
          fontFamily: 'var(--ff-display)',
          fontSize: 'var(--fs-24)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--tracking-tight)',
          color: 'var(--text)',
        }}
      >
        Choose an organization
      </h1>
      <p style={{ fontSize: 'var(--fs-13)', color: 'var(--text-mute)' }}>
        {email ? (
          <>
            Signed in as <span className="mono">{email}</span>
            {typeof count === 'number' && (
              <> · {count} organization{count === 1 ? '' : 's'}</>
            )}
          </>
        ) : (
          'Pick the organization you want to audit.'
        )}
      </p>
    </header>
  )
}

export function ChooserFooter({ disabled, onCreate }: { disabled: boolean; onCreate: () => void }) {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  return (
    <div
      style={{
        marginTop: 'var(--s-5)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--s-3)',
      }}
    >
      <Button variant="outline" size="sm" leadingIcon={Plus} onClick={onCreate} disabled={disabled}>
        Create or join organization
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          signOut()
          void navigate({ to: '/login' })
        }}
      >
        Sign out
      </Button>
    </div>
  )
}

/* ───────────────────────── list rows ───────────────────────── */

export function OrgRow({
  org,
  isNew,
  onChoose,
}: {
  org: Organization
  index?: number
  isNew: boolean
  onChoose: (org: Organization) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onChoose(org)}
      className="group flex w-full items-center text-left transition duration-150 hover:shadow-2 hover:-translate-y-0.5 border-line hover:border-line-strong motion-reduce:transform-none"
      style={{
        gap: 'var(--s-3)',
        padding: '14px 16px',
        background: 'var(--bg-elev)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 'var(--r-md)',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <Monogram name={org.name} />
      <OrgRowText org={org} />
      {isNew && <Badge tone="success">New</Badge>}
      <Badge tone={ROLE_TONE[org.role]}>{org.role}</Badge>
      <ArrowRight
        size={15}
        strokeWidth={1.75}
        aria-hidden="true"
        className="transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transform-none"
        style={{ flex: 'none', color: 'var(--text-faint)' }}
      />
    </button>
  )
}

function OrgRowText({ org }: { org: Organization }) {
  const created = org.createdAt
    ? formatDistanceToNowStrict(new Date(org.createdAt), { addSuffix: true })
    : null
  return (
    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        className="truncate"
        style={{ display: 'block', fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-medium)', color: 'var(--text)' }}
      >
        {org.name}
      </span>
      <span className="mono truncate" style={{ display: 'block', marginTop: 2, color: 'var(--text-mute)' }}>
        {org.id}
        {created && org.createdAt && (
          <>
            {' · '}
            <span title={org.createdAt}>created {created}</span>
          </>
        )}
      </span>
    </span>
  )
}

/** Deterministic two-letter avatar tinted from the categorical viz ramp. */
function Monogram({ name }: { name: string }) {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) % 997
  const viz = (hash % 8) + 1
  const words = name.trim().split(/\s+/)
  const initials =
    words.length >= 2 ? `${words[0][0]}${words[1][0]}` : name.slice(0, 2)
  return (
    <span
      aria-hidden="true"
      style={{
        width: 34,
        height: 34,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--r-sm)',
        background: `color-mix(in oklab, var(--viz-${viz}) 16%, var(--bg-elev))`,
        color: `color-mix(in oklab, var(--viz-${viz}) 55%, var(--text))`,
        fontSize: 'var(--fs-13)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
      }}
    >
      {initials}
    </span>
  )
}

/* ───────────────────────── load states ───────────────────────── */

export function OrgListSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-3)',
            padding: '14px 16px',
            background: 'var(--bg-elev)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <Skeleton width={18} height={12} />
          <Skeleton width={34} height={34} style={{ borderRadius: 'var(--r-sm)' }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width="45%" height={13} />
            <Skeleton width="65%" height={10} />
          </div>
          <Skeleton width={52} height={20} rounded />
        </div>
      ))}
    </div>
  )
}

export function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--s-3)',
        padding: 'var(--s-4)',
        background: 'var(--err-100)',
        border: '1px solid var(--err-200)',
        borderRadius: 'var(--r-md)',
        color: 'var(--err-700)',
        fontSize: 'var(--fs-13)',
      }}
    >
      <span>Could not load your organizations. Check your connection and retry.</span>
      <Button variant="outline" size="sm" leadingIcon={RefreshCw} onClick={onRetry}>
        Retry
      </Button>
    </div>
  )
}
